import type { CodeEditorProps } from '@chronosphereio/editors';
import { type ReactNode, useRef } from 'react';
import { QueryEditorContainer } from '../../QueryEditorContainer';
import { QueryEditorInput } from '../../QueryEditorInput';
import { QueryEditorInputContainer } from '../../QueryEditorInputContainer';
import { AIEditButton } from './AIEditButton';
import { AIEditPanel, type AIEditPanelProps } from './AIEditPanel';
import { AIEditReactionButtons } from './AIEditReactionButtons';
import { useAIEdit } from './ai-edit-context';
import { useAIEditShortcut } from './use-ai-edit-shortcut';

export interface AIEditContentProps extends AIEditPanelProps {
  extensions?: CodeEditorProps['extensions'];
  placeholder?: string;
  /** Additional end adornment content rendered before the AI edit button. */
  endAdornment?: ReactNode;
  /** Whether Cmd+I / Cmd+K keyboard shortcuts are active. Defaults to true. */
  shortcutEnabled?: boolean;
}

/**
 * Pre-composed editor content with AI edit wired in. Handles container ref
 * setup, keyboard shortcuts, and diff view derivation from the active
 * suggestion — eliminating boilerplate that every AI-edit consumer would
 * otherwise repeat.
 *
 * Must be rendered inside both a QueryEditor.Root and a QueryEditor.AIEditProvider.
 *
 * For consumers who need finer control over layout or slot composition,
 * the individual primitives (Container, InputContainer, Input, AIEditButton,
 * AIEditAcceptReject, AIEditPanel) are still available.
 */
export function AIEditContent({
  extensions,
  placeholder,
  endAdornment,
  shortcutEnabled = true,
  thinkingMessages,
}: AIEditContentProps) {
  const { suggestion } = useAIEdit();
  const containerRef = useRef<HTMLDivElement>(null);
  useAIEditShortcut({ enabled: shortcutEnabled, containerRef });

  const previousValue = suggestion ? suggestion.metadata?.existingQuery : undefined;

  return (
    <QueryEditorContainer ref={containerRef}>
      <QueryEditorInputContainer>
        <QueryEditorInput
          extensions={extensions}
          placeholder={placeholder}
          previousValue={previousValue}
          endAdornment={
            <>
              {endAdornment}
              <AIEditButton />
            </>
          }
        />
      </QueryEditorInputContainer>
      <AIEditReactionButtons />
      <AIEditPanel thinkingMessages={thinkingMessages} />
    </QueryEditorContainer>
  );
}
